import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { Adapt } from './lib/adapt.js';
import { SceneClock } from './lib/scene-clock.js';

// Contrat du rig partagé : interdit. L'abysse n'a d'autre lumière que la sienne.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'leviathan',
  family: 'organic',
  danceability: 0.26,
  requiresTier: 0,
  energyLevel: 0.12,
  bpmMin: 40,
  bpmMax: 120,
  movement: 0.15,
  tonality: 0.88,
  enabled: true
};

// LEVIATHAN : dans les abysses, une baleine à bosse faite de points de lumière
// glisse à travers l'écran, parfois avec son petit ; la neige marine tombe, de
// petites méduses pulsent, des faisceaux très faibles descendent de la surface.
// Ambient, néo-classique, drone, musique de film : AUCUNE impulsion (R9).
//
// LA BALEINE est un modèle paramétrique en unités de sa longueur L : un axe
// (u = 0 museau, u = 1 pédoncule), une section elliptique de rayon rad(u)
// (tête massive, pédoncule fin, petite bosse dorsale), deux longues pectorales
// à tubercules, une nageoire caudale en croissant avec son encoche. Les points
// sont tirés UNE fois (attributs statiques) : surface du corps, plus denses sur
// le dos ; lignes de lumière sur les contours (dos et ventre, bords des
// nageoires) ; sillons ventraux sur le flanc bas tourné vers nous. Le vertex
// shader applique la nage : une onde verticale qui court de la tête à la queue,
// d'amplitude croissante vers la queue, chaque section tournée selon la pente
// locale de la ligne médiane (la caudale suit donc la pente : elle bat). Les
// pectorales pivotent lentement autour de leur racine.
//
// LE CYCLE DE SCÈNE (lib/scene-clock.js, 25 s) : 18 s de passage puis 7 s
// d'abysse seul. La trajectoire est fermée en temps de passage τ : x linéaire
// (le museau entre à τ = 0, la queue sort à τ = 18), un léger arc vertical, et
// une profondeur qui CREUSE au milieu (elle passe derrière le centre, plus loin
// que la pochette, R19). Au premier cycle l'horloge démarre déjà dans le
// passage : à la première image, la baleine occupe le premier tiers de l'écran.
// Les passages suivants alternent le sens, et la distance (proche, plus grande
// et un peu floue ; lointaine, petite et plus floue).
//
// LE MOMENT FORT, à τ = 10 s : un grand coup de queue (une seconde onde, ample,
// sur l'arrière du corps) qui disperse un nuage de points depuis la caudale.
// Le sillage et ce nuage sont des particules dont la position de naissance est
// RECALCULÉE dans le shader (la trajectoire et la nage sont analytiques) : rien
// n'est téléversé par frame.
//
// LA MUSIQUE : adapt.intensity, lissée sur 4 s, règle la luminosité de la
// baleine (une respiration) et la densité de la neige. Rien n'est calé sur le
// temps : nage 6,3 s, pectorales 9 s, méduses 3 à 5 s.

const TAU = Math.PI * 2;
const FOV_DEG = 40;
const PASS_S = 18, GAP_S = 7, PERIOD_S = PASS_S + GAP_S;
const FLICK_S = 10;
const DEPTHS = [16, 11, 16, 24];          // pass k: default, near, default, far
const FOCUS = 16;
const UP_TILT = 0.17;                      // we see the whale a little from below
const SWIM_S = 6.3, CALF_SWIM_S = 4.1;
const TIERS = {
  0: { whale: 15000, calf: 1200, wake: 900, burst: 1400, snow: 2000 },
  1: { whale: 30000, calf: 2400, wake: 1500, burst: 2500, snow: 4000 },
  2: { whale: 30000, calf: 2400, wake: 1500, burst: 2500, snow: 4000 }
};

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function mulberry(seed) {
  let a = seed >>> 0;
  return () => { a = (a + 0x6D2B79F5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}
function hash01(k) { let h = (k * 2654435761) >>> 0; h ^= h >>> 15; h = Math.imul(h, 2246822519) >>> 0; h ^= h >>> 13; return (h >>> 0) / 4294967296; }

// --- the body, in units of L -----------------------------------------------------
// the same profile in JS (point placement) and GLSL (wake births)
function rad(u) {
  const h = Math.pow(Math.sin(Math.PI / 2 * Math.min(Math.max(u / 0.32, 0), 1)), 0.55);
  const t = u > 0.32 ? Math.pow(Math.max(1 - (u - 0.32) / 0.68, 0), 1.15) * 0.94 + 0.06 : 1;
  return 0.105 * h * t;
}
// a surface point (u, θ): θ = 0 on the back, π on the belly, sin θ > 0 on the side facing us
function bodyLoc(u, th) {
  const r = rad(u), c = Math.cos(th), s = Math.sin(th);
  let up = r * c * (c < 0 ? 0.85 : 1);
  if (c > 0.6) up += 0.022 * Math.exp(-Math.pow((u - 0.64) / 0.035, 2)) * c;     // the small dorsal hump
  return [0.5 - u, up, r * 0.82 * s];
}
const FLUKE_TWIST = 0.45;
function flukeLoc(c, ch) {
  // c spanwise -1..1, ch chordwise 0 (leading) .. 1 (trailing); swept back, notched
  const span = 0.21;
  const xle = -0.5 - 0.005 - 0.10 * Math.pow(Math.abs(c), 1.6);
  const chord = 0.085 * Math.pow(Math.max(1 - c * c, 0), 0.7) + 0.006;
  const notch = 0.022 * Math.exp(-Math.pow(c / 0.07, 2));
  const x = xle - ch * (chord - notch);
  const z = c * span;
  return [x, z * Math.sin(FLUKE_TWIST), z * Math.cos(FLUKE_TWIST)];
}

// kinds: 0 body, 1 contour / groove line (static), 2 pectoral fin (computed in the shader), 3 eye
function buildWhale(n, seed) {
  const rnd = mulberry(seed);
  const loc = new Float32Array(n * 3), info = new Float32Array(n * 4);
  let i = 0;
  const put = (l, kind, w, white, extra) => {
    if (i >= n) return;
    loc[i * 3] = l[0]; loc[i * 3 + 1] = l[1]; loc[i * 3 + 2] = l[2];
    info[i * 4] = kind; info[i * 4 + 1] = w; info[i * 4 + 2] = white; info[i * 4 + 3] = extra != null ? extra : rnd();
    i++;
  };
  const nBody = Math.round(n * 0.56), nContour = Math.round(n * 0.09), nGroove = Math.round(n * 0.07);
  const nFin = Math.round(n * 0.13), nFluke = Math.round(n * 0.09);
  // body surface: u by area (∝ rad), θ biased to the back
  for (let k = 0; k < nBody;) {
    const u = rnd(), th = rnd() * TAU;
    if (rnd() > rad(u) / 0.105) continue;
    if (rnd() > 0.5 + 0.5 * Math.cos(th) * 0.9) continue;
    const far = Math.sin(th) < -0.15;
    put(bodyLoc(u, th), 0, far ? 0.4 : 1.0, 0); k++;
  }
  // contour lines: the back and the belly (the silhouette seen from the side)
  for (let k = 0; k < nContour; k++) {
    const u = (k + rnd() * 0.5) / nContour * 2 % 1;
    const th = k % 2 === 0 ? 0 : Math.PI;
    put(bodyLoc(u, th + (rnd() - 0.5) * 0.04), 1, 1.15, 0.6);
  }
  // ventral grooves: 14 lines on the lower flank turned toward us, from the chin to the navel
  for (let k = 0; k < nGroove; k++) {
    const j = k % 14, u = 0.05 + 0.45 * rnd();
    const th = 1.95 + j * (1.05 / 13) + 0.03 * Math.sin(u * 30 + j);
    put(bodyLoc(u, th), 1, 1.05, 0.35);
  }
  // pectoral fins: (a along, b across) - placed in the shader, they pivot
  for (let k = 0; k < nFin; k++) {
    const side = k % 2 === 0 ? 1 : -1;
    const edge = rnd() < 0.3;
    // along the blade with a density ∝ its chord: uniform points piled up into a bright tip
    const a = edge ? rnd() : 1 - Math.pow(rnd(), 1 / 1.6);
    const b = edge ? (rnd() < 0.5 ? -1 : 1) : rnd() * 2 - 1;
    loc[i * 3] = a; loc[i * 3 + 1] = b; loc[i * 3 + 2] = side;
    info[i * 4] = 2; info[i * 4 + 1] = (edge ? 1.1 * (1 - 0.6 * a * a) : 0.8) * (side > 0 ? 1 : 0.45); info[i * 4 + 2] = edge ? 0.5 : 0; info[i * 4 + 3] = rnd();
    i++;
  }
  // the fluke: surface and outline
  for (let k = 0; k < nFluke; k++) {
    const edge = rnd() < 0.35;
    let c = rnd() * 2 - 1;
    // the surface spanwise ∝ the chord (the tips are thin), the outline uniform
    if (!edge) while (rnd() > Math.pow(Math.max(1 - c * c, 0), 0.7) + 0.07) c = rnd() * 2 - 1;
    const ch = edge ? (rnd() < 0.5 ? 0 : 1) : rnd();
    put(flukeLoc(c, ch), 1, edge ? 1.15 : 0.85, edge ? 0.55 : 0);
  }
  // the eye: a small bright knot
  while (i < Math.min(n, nBody + nContour + nGroove + nFin + nFluke + Math.round(n * 0.002))) {
    const l = bodyLoc(0.13 + (rnd() - 0.5) * 0.008, 1.75 + (rnd() - 0.5) * 0.08);
    put(l, 3, 1.6, 0.9);
  }
  // the rest: more back
  while (i < n) {
    const u = rnd(), th = (rnd() - 0.5) * 1.6;
    if (rnd() > rad(u) / 0.105) continue;
    put(bodyLoc(u, th), 0, 1.0, 0);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(loc, 3));
  g.setAttribute('aInfo', new THREE.BufferAttribute(info, 4));
  return g;
}

// wake and burst: (u, θ, r1, r2) + kind (0 wake, 1 burst)
function buildWake(nWake, nBurst, seed) {
  const rnd = mulberry(seed), n = nWake + nBurst;
  const a = new Float32Array(n * 3), b = new Float32Array(n * 4);
  for (let i = 0; i < n; i++) {
    const burst = i >= nWake;
    let u;
    if (burst) u = 0.72 + 0.4 * rnd();                     // the rear and the fluke
    else { u = rnd(); if (rnd() < 0.6) u = 0.45 + 0.55 * rnd(); }
    a[i * 3] = Math.min(u, 1.08); a[i * 3 + 1] = rnd() * TAU; a[i * 3 + 2] = burst ? 1 : 0;
    // a direction on the sphere for the drift
    const z = rnd() * 2 - 1, ph = rnd() * TAU, s = Math.sqrt(1 - z * z);
    b[i * 4] = s * Math.cos(ph); b[i * 4 + 1] = s * Math.sin(ph); b[i * 4 + 2] = z; b[i * 4 + 3] = rnd();
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(a, 3));
  g.setAttribute('aDir', new THREE.BufferAttribute(b, 4));
  return g;
}

function buildSnow(n, seed) {
  const rnd = mulberry(seed);
  const a = new Float32Array(n * 3), b = new Float32Array(n);
  for (let i = 0; i < n; i++) { a[i * 3] = rnd(); a[i * 3 + 1] = rnd(); a[i * 3 + 2] = rnd(); b[i] = rnd(); }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(a, 3));
  g.setAttribute('aR', new THREE.BufferAttribute(b, 1));
  return g;
}

function buildJellies(n, seed) {
  const rnd = mulberry(seed);
  const a = new Float32Array(n * 3), b = new Float32Array(n * 4);
  for (let i = 0; i < n; i++) {
    a[i * 3] = rnd(); a[i * 3 + 1] = rnd(); a[i * 3 + 2] = 0.2 + 0.8 * rnd();
    b[i * 4] = 3 + 2 * rnd(); b[i * 4 + 1] = rnd() * TAU; b[i * 4 + 2] = i % 3; b[i * 4 + 3] = rnd();
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(a, 3));
  g.setAttribute('aJ', new THREE.BufferAttribute(b, 4));
  return g;
}

// --- GLSL ------------------------------------------------------------------------
const WHALE_CHUNK = `
  #define PI 3.14159265
  uniform float uL, uD, uDz, uDir, uHalfW, uHalfH, uY0;
  uniform vec3 uUp;
  uniform float uFlick;
  float rad(float u) {
    float h = pow(sin(1.5707963 * clamp(u / 0.32, 0.0, 1.0)), 0.55);
    float t = u > 0.32 ? pow(max(1.0 - (u - 0.32) / 0.68, 0.0), 1.15) * 0.94 + 0.06 : 1.0;
    return 0.105 * h * t;
  }
  vec3 bodyLoc(float u, float th) {
    float r = rad(u), c = cos(th), s = sin(th);
    float up = r * c * (c < 0.0 ? 0.85 : 1.0);
    if (c > 0.6) up += 0.022 * exp(-pow((u - 0.64) / 0.035, 2.0)) * c;
    return vec3(0.5 - u, up, r * 0.82 * s);
  }
  // the pass, in camera space (the camera sits at the origin and looks down -z)
  vec3 pathC(float tau) {
    float s = tau / ${PASS_S.toFixed(1)}, sc = clamp(s, 0.0, 1.0);
    float x = uDir * (-uHalfW - 0.55 * uL + s * (2.0 * uHalfW + 1.15 * uL));
    return vec3(x, uY0 + 0.10 * uHalfH * sin(PI * sc), -(uD + uDz * sin(PI * sc)));
  }
  vec3 pathF(float tau) {
    float sc = clamp(tau / ${PASS_S.toFixed(1)}, 0.0, 1.0);
    return normalize(vec3(uDir * (2.0 * uHalfW + 1.15 * uL), 0.10 * uHalfH * PI * cos(PI * sc), -uDz * PI * cos(PI * sc)));
  }
  // the swimming wave (fraction of L), head to tail; the big stroke at tau = ${FLICK_S}
  float wave(float u, float swimT, float tau, float period) {
    float A = 0.028 * u * u + 0.004;
    float w = A * sin(5.65 * u - 6.2831853 * swimT / period);
    float E = uFlick * exp(-pow((tau - ${FLICK_S.toFixed(1)}) / 1.3, 2.0));
    w += E * 0.12 * pow(max(u, 0.0), 2.2) * sin(3.0 * u - 6.2831853 * (tau - ${FLICK_S.toFixed(1)}) / 2.6 + 0.6);
    return w;
  }
  void frame(vec3 f, out vec3 s, out vec3 up) {
    s = normalize(cross(f, uUp));
    if (s.z < 0.0) s = -s;                       // the side facing the camera
    up = normalize(cross(s, f));
    if (dot(up, uUp) < 0.0) up = -up;
  }
  // a local point (x along the body, head +0.5; up; side) to camera space
  vec3 place(vec3 loc, float swimT, float tau, float period, float L, vec3 C, vec3 f) {
    float u = clamp(0.5 - loc.x, 0.0, 1.2);
    float w = wave(u, swimT, tau, period);
    float sl = (wave(u + 0.01, swimT, tau, period) - wave(u - 0.01, swimT, tau, period)) / 0.02;
    float k = inversesqrt(1.0 + sl * sl);
    // the section turns with the local slope of the midline (the fluke beats with it)
    float x = loc.x + loc.y * sl * k;
    float y = w + loc.y * k;
    vec3 s, up; frame(f, s, up);
    return C + L * (f * x + up * y + s * loc.z);
  }
  // a pectoral fin: root on the lower flank, blade back-down-out, a slow pivot
  vec3 finLoc(float a, float b, float side, float swimT) {
    vec3 root = bodyLoc(0.27, 2.05);
    root.z *= side;
    float sweep = 0.22 * sin(6.2831853 * swimT / 9.0 + side * 1.3);
    vec3 d = normalize(vec3(-0.55, -0.50 + sweep, 0.62 * side));
    vec3 g = vec3(1.0, 0.25, 0.0);
    vec3 ch = normalize(g - d * dot(g, d));
    float chord = 0.052 * pow(max(1.0 - a, 0.0), 0.6) + 0.008;
    float tub = 0.006 * max(0.0, sin(a * 38.0));            // the tubercles on the leading edge
    float bb = b > 0.0 ? b * (chord * 0.6 + tub) : b * chord * 0.4;
    return root + d * a * 0.31 + ch * bb;
  }
`;

const whaleVertexShader = `
  precision highp float;
  ${WHALE_CHUNK}
  attribute vec4 aInfo;
  uniform float uTau, uSwimT, uPeriod, uScaleL, uBright, uPx, uFocus, uCocK, uVis;
  uniform vec3 uOffset;            // the calf: offset from the mother, in her frame (units of her L)
  varying vec4 vCol;
  void main() {
    vec3 loc = position;
    if (aInfo.x > 1.5 && aInfo.x < 2.5) loc = finLoc(position.x, position.y, position.z, uSwimT);
    vec3 C = pathC(uTau), f = pathF(uTau);
    vec3 s, up; frame(f, s, up);
    C += uL * (s * uOffset.z + up * uOffset.y + f * uOffset.x);
    vec3 P = place(loc, uSwimT, uTau, uPeriod, uL * uScaleL, C, f);
    vec4 mv = modelViewMatrix * vec4(P, 1.0);
    gl_Position = projectionMatrix * mv;
    float z = -mv.z;
    float coc = uCocK * abs(1.0 / z - 1.0 / uFocus);
    float size = 1.0 + coc;
    gl_PointSize = max(1.0, size * uPx);
    // a faint twinkle per point, slow
    float tw = 0.85 + 0.15 * sin(uSwimT * (0.4 + aInfo.w) + aInfo.w * 60.0);
    vec3 cyan = vec3(0.10, 0.42, 0.95);
    vec3 col = mix(cyan, vec3(0.75, 0.92, 1.0), aInfo.z);
    vCol = vec4(col * aInfo.y * uBright * tw * uVis * (uScaleL < 0.5 ? 1.35 : 1.0) / (size * size), 1.0);
  }
`;
const pointFragmentShader = `
  precision mediump float;
  varying vec4 vCol;
  void main() {
    vec2 p = gl_PointCoord * 2.0 - 1.0;
    float d = dot(p, p);
    if (d > 1.0) discard;
    gl_FragColor = vec4(vCol.rgb * (1.0 - 0.5 * d), 1.0);
  }
`;

const wakeVertexShader = `
  precision highp float;
  ${WHALE_CHUNK}
  attribute vec4 aDir;
  uniform float uTau, uSwimT, uBright, uPx, uFocus, uCocK;
  varying vec4 vCol;
  void main() {
    float burst = position.z;
    float age, tb;
    if (burst > 0.5) { tb = ${FLICK_S.toFixed(1)} + aDir.w * 0.8; age = uTau - tb; }
    else { float life = 6.0, off = aDir.w * life; tb = uTau - mod(uTau - off, life); age = uTau - tb; }
    float life = burst > 0.5 ? 9.0 : 6.0;
    float alive = step(0.0, tb) * step(0.0, age) * step(age, life);
    vec3 C = pathC(tb), f = pathF(tb);
    vec3 loc = position.x > 1.0 ? vec3(-0.5 - 0.06, 0.0, (aDir.x) * 0.2) : bodyLoc(position.x, position.y);
    vec3 P = place(loc, uSwimT - age, tb, ${SWIM_S.toFixed(1)}, uL, C, f);
    // drift: the wake lingers and sinks; the burst is thrown out and slowed by the water
    vec3 v = aDir.xyz;
    vec3 disp = burst > 0.5
      ? v * vec3(1.0, 1.4, 1.0) * 0.32 * 1.4 * (1.0 - exp(-age / 1.4)) + vec3(0.0, -0.012 * age, 0.0)
      : v * 0.025 * age + vec3(0.0, -0.01 * age, 0.0);
    P += uL * disp;
    vec4 mv = modelViewMatrix * vec4(P, 1.0);
    gl_Position = projectionMatrix * mv;
    float z = -mv.z;
    float size = 1.0 + uCocK * abs(1.0 / z - 1.0 / uFocus);
    gl_PointSize = max(1.0, size * uPx);
    float fade = smoothstep(0.0, 0.4, age) * (1.0 - smoothstep(life * 0.45, life, age));
    float gain = burst > 0.5 ? 1.6 : 0.9;
    vCol = vec4(vec3(0.25, 0.62, 1.0) * gain * fade * alive * uBright / (size * size), 1.0);
    if (alive < 0.5) gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
  }
`;

const snowVertexShader = `
  precision highp float;
  attribute float aR;
  uniform float uT, uTanH, uAspect, uPx, uFocus, uCocK, uDens;
  varying vec4 vCol;
  void main() {
    // depth: 5 % of the flakes in the foreground (1.3 to 4, big and out of focus: the depth
    // cue), the rest uniform in the frustum's volume from 4 to 34
    float z = position.z < 0.05 ? mix(1.3, 4.0, position.z / 0.05)
      : 34.0 * pow(mix(0.0016, 1.0, (position.z - 0.05) / 0.95), 1.0 / 3.0);
    float hh = z * uTanH * 1.08, hw = hh * uAspect;
    float v = 0.07 + 0.06 * aR;
    float y = (fract(position.y - v * uT / (2.0 * hh)) * 2.0 - 1.0) * hh;
    float x = (position.x * 2.0 - 1.0) * hw + 0.12 * sin(uT * (0.11 + 0.1 * aR) + aR * 40.0);
    vec4 mv = vec4(x, y, -z, 1.0);
    gl_Position = projectionMatrix * mv;
    float size = 1.0 + uCocK * abs(1.0 / z - 1.0 / uFocus);
    gl_PointSize = max(1.0, size * uPx);
    // more snow when the music swells: each flake has its threshold
    float on = smoothstep(aR - 0.06, aR + 0.06, uDens);
    // fades at the frustum's ends so nothing pops
    float edge = smoothstep(0.0, 0.08, fract(position.y - v * uT / (2.0 * hh))) * smoothstep(1.0, 0.92, fract(position.y - v * uT / (2.0 * hh)));
    vCol = vec4(vec3(0.48, 0.58, 0.68) * 0.55 * on * edge / (size < 3.0 ? size * size : 9.0 * pow(size / 3.0, 0.9)) * (size > 3.0 ? 1.8 : 1.0), 1.0);
  }
`;

const jellyVertexShader = `
  precision highp float;
  attribute vec4 aJ;
  uniform float uT, uTanH, uAspect, uPxH;
  varying vec3 vC;
  varying float vPulse, vLum;
  void main() {
    float z = 6.0 + 18.0 * position.z;
    float hh = z * uTanH, hw = hh * uAspect;
    float y = (fract(position.y + uT * 0.004 * (0.6 + aJ.w)) * 2.2 - 1.1) * hh;
    float x = (position.x * 2.0 - 1.0) * hw * 0.95 + 0.25 * sin(uT / 17.0 + aJ.y);
    gl_Position = projectionMatrix * vec4(x, y, -z, 1.0);
    float pulse = 0.5 + 0.5 * sin(6.2831853 * uT / aJ.x + aJ.y);
    vPulse = pulse;
    gl_PointSize = 0.9 / z * uPxH;
    vC = aJ.z < 0.5 ? vec3(0.25, 0.75, 1.0) : aJ.z < 1.5 ? vec3(0.65, 0.35, 1.0) : vec3(1.0, 0.35, 0.7);
    vLum = 0.5 + 0.5 * pulse;
  }
`;
const jellyFragmentShader = `
  precision highp float;
  uniform float uT;
  varying vec3 vC;
  varying float vPulse, vLum;
  void main() {
    vec2 p = gl_PointCoord * 2.0 - 1.0; p.y = -p.y;          // y up
    // the bell: a soft dome that narrows as it contracts
    float wv = 0.46 * (1.0 - 0.12 * vPulse);
    vec2 q = vec2(p.x / wv, (p.y - 0.15) / 0.30);
    float d = length(q);
    float dome = (p.y > 0.08 ? 1.0 : smoothstep(-0.05, 0.08, p.y)) * (1.0 - smoothstep(0.75, 1.0, d));
    float rim = dome * smoothstep(0.55, 0.95, d);
    // trailing tentacles: four soft wavy threads
    float t = 0.0;
    for (int i = 0; i < 4; i++) {
      float xi = (float(i) - 1.5) * 0.12 * (1.0 - 0.1 * vPulse) + 0.04 * sin(p.y * 9.0 + uT * 1.3 + float(i) * 2.0);
      t += (1.0 - smoothstep(0.0, 0.035, abs(p.x - xi))) * smoothstep(0.1, -0.05, p.y) * smoothstep(-0.95, -0.3, p.y);
    }
    float a = (dome * 0.22 + rim * 0.35 + t * 0.12) * vLum;
    if (a < 0.003) discard;
    gl_FragColor = vec4(vC * a * 0.55, 1.0);
  }
`;

// the abyss: the night-blue gradient, lighter toward the surface, and faint beams
const bgVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.999, 1.0); }
`;
const bgFragmentShader = `
  precision highp float;
  uniform float uT, uAspect;
  varying vec2 vUv;
  void main() {
    float y = vUv.y;
    vec3 deep = vec3(0.0002, 0.0007, 0.0020), surf = vec3(0.0030, 0.0120, 0.0300);
    vec3 col = mix(deep, surf, pow(y, 1.8));
    // beams from the surface, slanted, slowly wandering and rippling (R14: 1 - exp(-density))
    float x = (vUv.x - 0.5) * uAspect;
    float dens = 0.0;
    for (int i = 0; i < 4; i++) {
      float fi = float(i);
      float c = (fi - 1.5) * 0.55 * uAspect / 1.78 + 0.18 * sin(uT / (23.0 + 7.0 * fi) + fi * 2.1);
      float xr = x - c + (1.0 - y) * (0.22 + 0.05 * fi);
      float w = 0.05 + 0.025 * fi + 0.02 * sin(uT / 11.0 + fi);
      float rip = 0.65 + 0.35 * sin(y * 7.0 + uT * 0.21 + fi * 1.7) * sin(y * 3.1 - uT * 0.13 + fi);
      dens += exp(-xr * xr / (w * w)) * rip * smoothstep(0.15, 1.0, y) * (0.7 + 0.3 * fi / 3.0);
    }
    float a = 1.0 - exp(-dens * 0.9);
    col = mix(col, vec3(0.010, 0.030, 0.050), a * 0.6);
    gl_FragColor = vec4(col, 1.0);
  }
`;

const displayVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const displayFragmentShader = `
  precision highp float;
  uniform sampler2D uTex;
  uniform float uAspect;
  uniform vec2 uSafeCenter, uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  void main() {
    vec3 col = texture2D(uTex, vUv).rgb;
    // behind the real cover the abyss dims: the whale passes BEHIND it (R19: the mask follows the cover)
    vec2 qq = abs((vUv * 2.0 - 1.0) * vec2(uAspect, 1.0) - uSafeCenter) - uSafeHalfSize;
    float dz = length(max(qq, 0.0)) + min(max(qq.x, qq.y), 0.0);
    col *= mix(0.35, 1.0, smoothstep(0.0, uSafeFeather, dz));
    // a little dither: the dark gradient must not band
    float n = fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453);
    col += (n - 0.5) * 0.0012 * (0.2 + col * 4.0);
    gl_FragColor = vec4(max(col, 0.0), 1.0);
  }
`;

export class LeviathanVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIERS[o.qualityTier] ? o.qualityTier : 1;
    this._N = TIERS[this._tier];
    this._forcePass = urlParam('levpass') === '1';
    this._forceCalf = urlParam('levcalf') === '1';
    this._adapt = new Adapt();
    this._post = { bloomStrength: 0.3, bloomThreshold: 0.8, vignette: 0.35 };
    if (urlParam('levprobe') === '1' && typeof window !== 'undefined') window.__leviathan = this;
  }

  get postProcessing() { return this._post; }

  init(scene) {
    this._scene = scene;
    const N = this._N;
    this._cam = new THREE.PerspectiveCamera(FOV_DEG, 1.78, 0.5, 80);
    this._private = new THREE.Scene();
    const tanH = Math.tan(FOV_DEG / 2 * Math.PI / 180);
    this._tanH = tanH;

    const W = {
      uL: { value: 11 }, uD: { value: 16 }, uDz: { value: 4 }, uDir: { value: 1 }, uHalfW: { value: 10 }, uHalfH: { value: 5.8 }, uY0: { value: 0 },
      uUp: { value: new THREE.Vector3(0, Math.cos(UP_TILT), -Math.sin(UP_TILT)) }, uFlick: { value: 1 },
      uTau: { value: 0 }, uSwimT: { value: 0 }, uBright: { value: 1 }, uPx: { value: 1 }, uFocus: { value: FOCUS }, uCocK: { value: 70 }
    };
    this._W = W;
    const whaleU = Object.assign({}, W, { uPeriod: { value: SWIM_S }, uScaleL: { value: 1 }, uOffset: { value: new THREE.Vector3() }, uVis: { value: 1 } });
    const calfU = Object.assign({}, W, { uPeriod: { value: CALF_SWIM_S }, uScaleL: { value: 0.33 }, uOffset: { value: new THREE.Vector3(-0.11, -0.16, 0.16) }, uVis: { value: 0 } });
    this._calfU = calfU;
    const additive = { transparent: true, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false };

    // the abyss first
    this._bgU = { uT: { value: 0 }, uAspect: { value: 1.78 } };
    this._bg = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._bgU, vertexShader: bgVertexShader, fragmentShader: bgFragmentShader, depthTest: false, depthWrite: false }));
    this._bg.frustumCulled = false; this._bg.renderOrder = 0;
    // the far snow, the jellies, the whale and her calf, the wake, the near snow: one draw call each
    this._snowU = { uT: { value: 0 }, uTanH: { value: tanH }, uAspect: { value: 1.78 }, uPx: { value: 1 }, uFocus: { value: FOCUS }, uCocK: { value: 23 }, uDens: { value: 0.7 } };
    this._snow = new THREE.Points(buildSnow(N.snow, 991), new THREE.ShaderMaterial(Object.assign({ uniforms: this._snowU, vertexShader: snowVertexShader, fragmentShader: pointFragmentShader }, additive)));
    this._jellyU = { uT: { value: 0 }, uTanH: { value: tanH }, uAspect: { value: 1.78 }, uPxH: { value: 1000 } };
    this._jelly = new THREE.Points(buildJellies(7, 77), new THREE.ShaderMaterial(Object.assign({ uniforms: this._jellyU, vertexShader: jellyVertexShader, fragmentShader: jellyFragmentShader }, additive)));
    this._whale = new THREE.Points(buildWhale(N.whale, 4242), new THREE.ShaderMaterial(Object.assign({ uniforms: whaleU, vertexShader: whaleVertexShader, fragmentShader: pointFragmentShader }, additive)));
    this._calf = new THREE.Points(buildWhale(N.calf, 5151), new THREE.ShaderMaterial(Object.assign({ uniforms: calfU, vertexShader: whaleVertexShader, fragmentShader: pointFragmentShader }, additive)));
    this._wake = new THREE.Points(buildWake(N.wake, N.burst, 6161), new THREE.ShaderMaterial(Object.assign({ uniforms: W, vertexShader: wakeVertexShader, fragmentShader: pointFragmentShader }, additive)));
    let order = 1;
    for (const m of [this._bg, this._snow, this._jelly, this._whale, this._calf, this._wake]) { m.frustumCulled = false; m.renderOrder = order++; this._private.add(m); }

    this._target = null;
    this._dispU = {
      uTex: { value: null }, uAspect: safeZoneUniforms.uAspect,
      uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._dispU, vertexShader: displayVertexShader, fragmentShader: displayFragmentShader, depthTest: false, depthWrite: false }));
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._display);

    // the scene clock: the first pass is already under way, the whale a third into the frame
    this._layout();
    const halfW = W.uHalfW.value, L = W.uL.value;
    const s0 = this._forcePass ? 0 : (2 * halfW / 3 + 0.05 * L) / (2 * halfW + 1.15 * L);
    this._clock = new SceneClock({ period: PERIOD_S, start: s0 * PASS_S / PERIOD_S });
    this._pass = -1;
    this._intensity = 0.3;
    this._log = [];
    this._applyPass();
  }

  // the frame's geometry from the aspect: L, half extents at the pass depth
  _layout() {
    const W = this._W, a = safeZoneUniforms.uAspect.value || 1.78;
    const D = W.uD.value;
    const halfH = D * this._tanH, halfW = halfH * a;
    W.uHalfH.value = halfH; W.uHalfW.value = halfW;
    // a whale over half the width at the default depth; in portrait, 85 % of the width
    const halfWDef = FOCUS * this._tanH * a;
    W.uL.value = Math.min(11, 2 * halfWDef * 0.85);
    W.uY0.value = -0.05 * halfH;
    W.uDz.value = 0.25 * D;
    this._bgU.uAspect.value = a; this._snowU.uAspect.value = a; this._jellyU.uAspect.value = a;
  }

  _applyPass() {
    // the pass number from the scene time (so ?scenet=<s> reaches any pass)
    const k = Math.floor(this._clock.elapsed / PERIOD_S);
    if (k === this._pass) return;
    this._pass = k;
    const W = this._W;
    W.uDir.value = k % 2 === 0 ? 1 : -1;
    W.uD.value = DEPTHS[k % DEPTHS.length];
    this._calfOn = this._forceCalf || hash01(k + 17) < 0.5;
    this._layout();
    this._log.push({ what: 'pass', k, dir: W.uDir.value, D: W.uD.value, calf: this._calfOn });
    if (this._log.length > 20) this._log.shift();
  }

  update(deltaTime, profile) {
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    const playing = profile.playbackState === 'PLAYING';
    const a = this._adapt.update(playing ? dt : 0, profile);
    if (!playing) return;                                   // frozen, never black
    this._clock.update(dt);
    this._applyPass();
    this._layout();
    // the breath: intensity smoothed over 4 s - no beat, no onset
    this._intensity += (a.intensity - this._intensity) * Math.min(1, dt / 4);
    const I = this._intensity;
    const W = this._W, t = this._clock.elapsed;
    W.uTau.value = this._clock.t;
    W.uSwimT.value = t;
    W.uBright.value = (0.95 + 0.75 * I) * (W.uD.value > 20 ? 0.8 : 1);
    // the calf fades in at the pass start, out after it
    const tau = this._clock.t;
    const want = this._calfOn && tau < PASS_S + 2 ? 1 : 0;
    this._calfU.uVis.value += (want - this._calfU.uVis.value) * Math.min(1, dt / 1.5);
    if (tau < 0.1 && this._calfOn) this._calfU.uVis.value = 1;
    this._snowU.uT.value = t; this._snowU.uDens.value = 0.45 + 0.55 * I;
    this._jellyU.uT.value = t; this._bgU.uT.value = t;
  }

  _render(renderer) {
    const cur = renderer.getRenderTarget();
    let w, h;
    if (cur) { w = cur.width; h = cur.height; } else { const b = renderer.getDrawingBufferSize(_size); w = b.x; h = b.y; }
    const cam = this._cam;
    if (Math.abs(cam.aspect - w / h) > 1e-4) { cam.aspect = w / h; cam.updateProjectionMatrix(); }
    const px = Math.max(0.6, h / 1080);
    this._W.uPx.value = px; this._snowU.uPx.value = px;
    this._jellyU.uPxH.value = h / (2 * this._tanH);
    if (!this._target || this._target.width !== w || this._target.height !== h) {
      if (this._target) this._target.dispose();
      // half float: the abyss is a few thousandths of linear light, 8 bits would band it
      this._target = new THREE.WebGLRenderTarget(w, h, { type: THREE.HalfFloatType, depthBuffer: false, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
    }
    const cc = renderer.getClearColor(_cc), ca = renderer.getClearAlpha();
    const xr = renderer.xr.enabled; renderer.xr.enabled = false;
    renderer.setClearColor(0x000000, 1);
    renderer.setRenderTarget(this._target);
    renderer.clear(true, false, false);
    renderer.render(this._private, cam);
    renderer.setRenderTarget(cur);
    renderer.setClearColor(cc, ca);
    renderer.xr.enabled = xr;
    this._dispU.uTex.value = this._target.texture;
  }

  dispose() {
    if (this._scene && this._display) { this._display.onBeforeRender = null; this._scene.remove(this._display); }
    for (const m of [this._display, this._bg, this._snow, this._jelly, this._whale, this._calf, this._wake]) if (m) { m.geometry.dispose(); m.material.dispose(); }
    if (this._target) this._target.dispose();
    if (this._adapt) this._adapt.dispose();
    if (typeof window !== 'undefined' && window.__leviathan === this) window.__leviathan = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
const _cc = new THREE.Color();
